import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, User, FileText, Shield, Edit, Trash2, Upload, Badge, QrCode, Eye, MoreHorizontal } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../../components/ui/custom-dropdown-menu';
import { StatusBadge } from '../../../../../components/ui/StatusBadge';
import { cn } from '../../../../../lib/utils';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { TeamMemberFilters } from './TeamMemberFilters';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { TeamMemberDocumentModal } from './TeamMemberDocumentModal';
import { TeamMemberCertificateUploadModal } from './TeamMemberCertificateUploadModal';
import { TeamMember } from '../../../types';
import RoleAccessMatrix from './RoleAccessMatrix';
import { Dialog, DialogContent } from '../../../../../components/ui/dialog';
import { useUserPermissionsAfterLogin } from '../../../../admin/hooks/useUserPermissionsAfterLogin';
import { EquipmentSignatureReportModal } from '../equipment/EquipmentSignatureReportModal';
import { GenerateReportButton } from '../../../../../components/ui/GenerateReportButton';
import { EquipmentReportsViewModal } from '../equipment/EquipmentReportsViewModal';

interface ProjectTeamProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
}

const ROLE_ICONS = {
  'Supervisor': Shield,
  'Welder': User,
  'Inspector': FileText,
} as const;

const TEAM_STATUS_COLORS: Record<string, string> = {
  active: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  inactive: 'text-heritage-grey bg-[rgba(150,150,150,0.08)]',
  suspended: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  pending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
};
const TEAM_STATUS_DEFAULT = 'text-heritage-grey bg-[rgba(150,150,150,0.08)]';

export const ProjectTeam: React.FC<ProjectTeamProps> = ({ project, canEdit = true, canReport = true }) => {
  // Document modal state
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedTeamMemberForDocs, setSelectedTeamMemberForDocs] = useState<TeamMember | null>(null);

  // Certificate upload modal state
  const [isCertificateUploadModalVisible, setIsCertificateUploadModalVisible] = useState(false);
  const [selectedTeamMemberForUpload, setSelectedTeamMemberForUpload] = useState<TeamMember | null>(null);

  // Role access matrix modal state
  const [isRoleAccessMatrixVisible, setIsRoleAccessMatrixVisible] = useState(false);

  // QR code viewer modal state
  const [isQRCodeModalVisible, setIsQRCodeModalVisible] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState<string | null>(null);

  // Track if changes were made to avoid unnecessary refreshes
  const [hasChanges, setHasChanges] = useState(false);

  // QC Inspection Personnel report modal state
  const [isQCReportModalOpen, setIsQCReportModalOpen] = useState(false);
  const [isReportsViewModalOpen, setIsReportsViewModalOpen] = useState(false);

  // Qualification modal state
  const [isQualificationModalVisible, setIsQualificationModalVisible] = useState(false);
  const [selectedMemberQualifications, setSelectedMemberQualifications] = useState<any>(null);

  // Now use the Redux hook with all the data
  const {
    filteredTeamMembers,
    selectedTeamMember,
    isAddingTeamMember,
    filters,
    hasActiveFilters,
    setSelectedTeamMember,
    setIsAddingTeamMember,
    setFilters,
    handleTeamMemberDocumentUpload,
    handleSaveTeamMember,
    handleEditTeamMember,
    handleViewTeamMemberDocuments,
    handleAddNewTeamMember,
    fetchTeamMembers,
    projectRoles,
    certifications,
    rolesLoading,
    certificationsLoading,
    fetchProjectRoles,
    fetchCertifications,
    deleteTeamMember,
    loading,
    error,
    deleteLoading,
    addLoading,
    updateLoading,
    createUpdateLoading,
    isRefreshingData,
    deleteCertLoading,
    clearTeamMembersError
  } = useTeamMembersReduxHook(undefined, project.project.projectId);

  // Get user permissions
  const { isManagerOrHigher, userPermissions, isAdminOrHigher } = useUserPermissionsAfterLogin();

  // User role flags for the QC report modal
  const isCustomerRepresentativeUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Customer Representative';
  }, [project]);

  const isQCInspectorUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'QC Inspector' ||
      (project.project as any)?.team_member_role === 'Quality Control' ||
      (project.project as any)?.team_member_role === 'QA/QC';
  }, [project]);

  const isWelderUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Welder' ||
      (project.project as any)?.team_member_role === 'Welding';
  }, [project]);

  const currentUserTeamMemberId = useMemo(() => {
    return userPermissions?.user_id || null;
  }, [userPermissions]);

  const isAdminOrSuperAdmin = useMemo(() => {
    return isAdminOrHigher();
  }, [isAdminOrHigher]);

  // Memoized onClose function to prevent infinite loops
  const handleDocumentModalClose = useCallback(() => {
    console.log('Closing document modal');
    setIsDocumentModalVisible(false);
    setSelectedTeamMemberForDocs(null);
    // Refresh team members table after closing modal
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Helper function to transform team member documents for the modal
  const transformTeamMemberDocumentsForModal = (member: TeamMember) => {
    // Only use API certificates directly, don't transform documents
    const certificates = member.apiData?.certificates || [];


    // Transform only certificates from API data (no documents transformation)
    const transformedCertificates = certificates.map((cert: any) => ({
      id: `cert_${cert.cert_id}`,
      fileName: cert.file_name,
      type: 'certification' as const,
      uploadDate: cert.created_at,
      s3Key: cert.s3Key,
      file_url: cert.file_url,
      document_tags: cert.document_tags || [],
      cert_id: cert.cert_id,
      user_id: cert.user_id,
      team_member_id: cert.team_member_id
    }));


    // Return only certificates, no documents
    return transformedCertificates;
  };

  // Document handling functions
  const handleViewDocuments = (member: TeamMember) => {

    setSelectedTeamMemberForDocs(member);
    setIsDocumentModalVisible(true);
  };

  const handleUploadCertificates = (member: TeamMember) => {

    // Check if certifications are loaded
    if (!certifications || certifications.length === 0) {
      customNotification.warning('Certifications not loaded', 'Please wait for certifications to load before uploading certificates.');
      return;
    }

    setSelectedTeamMemberForUpload(member);
    setIsCertificateUploadModalVisible(true);
  };

  const handleViewQRCode = (qrCodeUrl: string | undefined) => {
    if (!qrCodeUrl) {
      customNotification.warning('QR Code Not Available', 'No QR code found for this welder.');
      return;
    }
    setSelectedQRCode(qrCodeUrl);
    setIsQRCodeModalVisible(true);
  };

  const handleDocumentDownload = (doc: any) => {
    // Handle document download logic here

    // If the document has a file_url, use it for direct download
    if (doc.file_url) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = doc.file_url;
      link.download = doc.fileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      customNotification.success('Download Started', `Downloading ${doc.fileName}...`);
    } else if (doc.s3Key) {
      // If no file_url but has s3Key, show info about future implementation
      customNotification.info('Download', `Download functionality for ${doc.fileName} will be implemented soon using S3 key.`);
    } else {
      // No download method available
      customNotification.warning('Download Unavailable', `No download method available for ${doc.fileName}.`);
    }
  };



  // Extract roles and project roles data from Redux state
  const roles = React.useMemo(() => {
    if (!projectRoles || projectRoles.length === 0) return [];
    // Extract roles from nested structure: projectRoles[0].projectRolePermissions.roles
    const firstEntry = projectRoles[0];
    const rolesArray = firstEntry?.projectRolePermissions?.roles || [];
    return rolesArray.map((role: any) => role.projectRoleName || role.project_role_name).filter(Boolean);
  }, [projectRoles]);

  const projectRolesData = React.useMemo(() => {
    if (!projectRoles || projectRoles.length === 0) return [];
    // Extract roles from nested structure: projectRoles[0].projectRolePermissions.roles
    const firstEntry = projectRoles[0];
    const rolesArray = firstEntry?.projectRolePermissions?.roles || [];
    // Get projectLevelRoleId from parent entry
    const projectLevelRoleId = firstEntry?.projectLevelRoleId || firstEntry?.project_level_role_id;
    // Map roles to the expected format
    return rolesArray.map((role: any) => ({
      projectLevelRoleId: projectLevelRoleId,
      projectRoleName: role.projectRoleName || role.project_role_name,
      isActive: firstEntry?.isActive !== false,
      permissions: role.permissions || {}
    })).filter((role: any) => role.projectRoleName);
  }, [projectRoles]);

  // Load roles and certifications on component mount
  React.useEffect(() => {
    fetchProjectRoles();
    fetchCertifications();
  }, [fetchProjectRoles, fetchCertifications]);

  // Monitor delete operation completion and refresh data
  React.useEffect(() => {

    if (!deleteLoading && hasChanges) {
      // Saga already refreshed list; just reset flag
      setHasChanges(false);
    }
  }, [deleteLoading, hasChanges]);

  // Monitor create/update operation completion and data refresh
  React.useEffect(() => {

    // When create/update loading starts, set hasChanges to true
    if (createUpdateLoading) {
      setHasChanges(true);
    }

    // When both create/update and refresh are complete, reset hasChanges
    if (!createUpdateLoading && !isRefreshingData && hasChanges) {
      setHasChanges(false);
    }
  }, [createUpdateLoading, isRefreshingData, hasChanges]);



  // Add handlers for new items
  const [newRole, setNewRole] = React.useState('');
  const [newCertification, setNewCertification] = React.useState('');
  const [isAddingRole, setIsAddingRole] = React.useState(false);
  const [isAddingCertification, setIsAddingCertification] = React.useState(false);

  // Delete state and handlers
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [teamMemberToDelete, setTeamMemberToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Monitor delete operation completion and handle modal closure
  React.useEffect(() => {
    if (!deleteLoading && isDeleting) {
      // Delete API response received (success or failure)
      // Close delete modal and reset delete-related state only
      setIsDeleting(false);
      setIsDeleteModalVisible(false);
      setTeamMemberToDelete(null);

      // Don't close other modals or call refresh API
      // Just close the delete confirmation modal after API response
    }
  }, [deleteLoading, isDeleting]);

  const handleDeleteTeamMember = async (member: any) => {
    setTeamMemberToDelete(member);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!teamMemberToDelete) return;

    setIsDeleting(true);
    try {
      // Call delete API only - no automatic refresh
      deleteTeamMember({
        teamMemberId: teamMemberToDelete.team_member_id || teamMemberToDelete.id,
        projectId: project.project.projectId
      });

      // Modal will close when API response is received (handled by useEffect)

    } catch (error) {
      customNotification.error('Error deleting team member', 'An unexpected error occurred. Please try again.');
      setIsDeleting(false);
      setIsDeleteModalVisible(false);
      setTeamMemberToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setTeamMemberToDelete(null);
  };

  const handleAddRole = () => {
    if (newRole.trim()) {
      // Note: Adding roles would require API implementation
      // For now, just show a notification
      customNotification.info('Feature not implemented', 'Adding new roles requires API implementation.');
      setNewRole('');
      setIsAddingRole(false);
    }
  };

  const handleAddCertification = () => {
    if (newCertification.trim()) {
      // Note: Adding certifications would require API implementation
      // For now, just show a notification
      customNotification.info('Feature not implemented', 'Adding new certifications requires API implementation.');
      setNewCertification('');
      setIsAddingCertification(false);
    }
  };

  // Handle reports view button click - opens the QC reports view modal
  const handleReportsView = useCallback(() => {
    setIsReportsViewModalOpen(true);
  }, []);

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-4 sm:p-6">
        {!isAddingTeamMember && <div className="space-y-6">
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {/* {canReport && <Button
                onClick={() => handleReportsView()}
                // size="sm"
                // className="bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90 flex items-center gap-2 min-w-[140px] justify-center w-full sm:w-auto"
              >
                <FileText className="h-4 w-4" />
                 QC Inspection Reports
              </Button>}
              {canReport && <GenerateReportButton
                moduleName="QC Inspection Report"
                onClick={() => setIsQCReportModalOpen(true)}
              />}
              */}
            {canEdit && (
              <Button
                onClick={() => setIsAddingTeamMember(true)}
                size="sm"
                className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
              >
                <Plus className="h-4 w-4" />
                Add Team Member
              </Button>
            )}
            {isManagerOrHigher() && (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                  <MoreHorizontal className="h-4 w-4 text-text-grey" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsRoleAccessMatrixVisible(true)}>
                    <Shield className="h-4 w-4 mr-2" />
                    Add Access Roles
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="space-y-4">
            <TeamMemberFilters
              filters={filters}
              onFiltersChange={setFilters}
              roles={roles}
              filteredCount={filteredTeamMembers.length}
            />

            {loading || isRefreshingData || deleteCertLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
                  <span className="text-[13px] text-heritage-grey">
                    {deleteCertLoading ? 'Deleting certificate...' :
                      isRefreshingData ? 'Refreshing team members...' :
                        'Loading team members...'}
                  </span>
                </div>
              </div>
            ) : error && error.type === 'FETCH_ERROR' ? (
              // Only show error message for fetch errors, not for create/update errors
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="text-readable-red mb-2">Error loading team members</div>
                  <div className="text-[12px] text-heritage-grey mb-3">
                    {error.message}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchTeamMembers()}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : deleteLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
                  <span className="text-[13px] text-heritage-grey">Loading team members...</span>
                </div>
              </div>
            ) : filteredTeamMembers.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="text-[13px] text-heritage-grey mb-2">
                    {hasActiveFilters ? 'No team members match the current filters' : 'No team members found'}
                  </div>
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ search: '', role: [], status: [] })}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="p-4">
                  <div className="relative overflow-x-auto">
                    <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                      <Table containerClassName="">
                    <TableHeader className="sticky top-0 z-20 bg-white">
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Certificates</TableHead>
                        {/* <TableHead>Qualifications</TableHead> */}
                        <TableHead className="sticky right-0 bg-white">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTeamMembers.map((member: any) => {
                        const statusKey = (member.status || 'active').toString().toLowerCase();
                        const statusClass = TEAM_STATUS_COLORS[statusKey] || TEAM_STATUS_DEFAULT;
                        return (
                          <TableRow key={member.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="text-[13px] font-medium text-text-grey">{member.apiData?.user ? `${member.apiData.user.first_name} ${member.apiData.user.last_name}`.trim() : member.name || '-'}</div>
                                {member.role === 'Welder' && (
                                  <Button variant="outline" title="Welder QR Code" className="h-7 w-7 p-0 flex-shrink-0" onClick={() => handleViewQRCode(member.apiData?.team_member_permissions?.qr_code)}>
                                    <QrCode className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-[13px] text-text-grey">{member.role || '-'}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-[13px] text-text-grey">{member.apiData?.user?.email || member.email || '-'}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-[13px] text-text-grey">{member.apiData?.user?.phone_number || member.phone || '-'}</div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={member.status || 'active'} className={statusClass} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleUploadCertificates(member)}
                                  title="Upload Certificates"
                                  variant="outline"
                                  disabled={!canEdit}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                                {member.apiData?.certificates && member.apiData?.certificates.length > 0 && <Button
                                  className={`flex items-center gap-1 w-8 h-8 p-0 flex items-center justify-center ${member.apiData?.certificates && member.apiData?.certificates.length > 0
                                    ? 'text-blue-600 hover:text-blue-800'
                                    : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                  title={member.apiData?.certificates && member.apiData?.certificates.length > 0
                                    ? "View Certificates"
                                    : "No certificates to view"
                                  }
                                  variant="outline"
                                  onClick={() => {
                                    if (member.apiData?.certificates && member.apiData?.certificates.length > 0) {
                                      handleViewDocuments(member);
                                    }
                                  }}
                                >
                                  <FileText className="h-4 w-4" />
                                  {member.apiData?.certificates?.length || 0}
                                </Button>
                                }
                              </div>
                            </TableCell>
                            {/* <TableCell>
                              <div className="flex items-center justify-center">
                                {(member.role === 'QA/QC' || member.role === 'Quality Control') && 
                                 member.apiData?.inspection_personal_details && (
                                  <Button
                                    variant="outline"
                                    className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => {
                                      setSelectedMemberQualifications({
                                        name: member.apiData?.user ? `${member.apiData.user.first_name} ${member.apiData.user.last_name}` : member.name,
                                        details: member.apiData.inspection_personal_details
                                      });
                                      setIsQualificationModalVisible(true);
                                    }}
                                    title="View Qualifications"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell> */}
                            <TableCell className="sticky right-0 bg-white">
                              <div className="flex items-center gap-2">
                                <Button
                                  onClick={() => handleEditTeamMember(member)}
                                  className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Edit Team Member"
                                  variant="outline"
                                  disabled={!canEdit}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleDeleteTeamMember(member)}
                                  title="Delete Team Member"
                                  variant="outline"
                                  disabled={!canEdit || deleteLoading}
                                >
                                  {deleteLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-hero-red"></div>
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>}

        {isAddingTeamMember && <AddTeamMemberModal
          isOpen={isAddingTeamMember}
          onOpenChange={(open) => {
            setIsAddingTeamMember(open);
            // If closing the modal, reset the selected team member to default state
            if (!open) {
              setSelectedTeamMember({
                id: '',
                name: '',
                email: '',
                phone: '',
                role: '',
                certifications: [],
                qualifications: [],
                documents: [],
                status: 'active'
              });
              // Reset new role and certification inputs
              setNewRole('');
              setNewCertification('');
            }
          }}
          onBack={() => {
            setIsAddingTeamMember(false);
            setSelectedTeamMember({
              id: '',
              name: '',
              email: '',
              phone: '',
              role: '',
              certifications: [],
              qualifications: [],
              documents: [],
              status: 'active'
            });
            setNewRole('');
            setNewCertification('');
          }}
          selectedTeamMember={selectedTeamMember}
          setSelectedTeamMember={setSelectedTeamMember}
          handleTeamMemberDocumentUpload={handleTeamMemberDocumentUpload}
          handleSaveTeamMember={handleSaveTeamMember}
          refreshTeamMembers={fetchTeamMembers}
          roles={roles}
          projectRolesData={projectRolesData}
          certifications={certifications}
          isAddingRole={isAddingRole}
          setIsAddingRole={setIsAddingRole}
          isAddingCertification={isAddingCertification}
          setIsAddingCertification={setIsAddingCertification}
          newRole={newRole}
          setNewRole={setNewRole}
          newCertification={newCertification}
          setNewCertification={setNewCertification}
          handleAddRole={handleAddRole}
          handleAddCertification={handleAddCertification}
          projectId={Number(project.project.projectId)}
          isLoading={addLoading || updateLoading || createUpdateLoading || isRefreshingData}
          onChangesMade={() => setHasChanges(true)}
        />
        }
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteModalVisible}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Team Member"
        itemType="Team Member"
        itemName={teamMemberToDelete?.apiData?.user ? `${teamMemberToDelete.apiData.user.first_name} ${teamMemberToDelete.apiData.user.last_name}`.trim() : teamMemberToDelete?.name}
        warningMessage="This team member will be permanently removed from the project. All associated data and documents will be lost."
        detailsRenderer={(item) => (
          <div className="bg-page-bg p-3 rounded-md">
            <p className="text-[12px] font-medium text-heritage-grey mb-2">Team Member details:</p>
            <div className="space-y-1">
              <p className="text-[13px] text-text-grey flex items-center">
                <span className="w-2 h-2 bg-heritage-grey rounded-full mr-2"></span>
                Name: {item.apiData?.user ? `${item.apiData.user.first_name} ${item.apiData.user.last_name}`.trim() : item.name}
              </p>
              <p className="text-[13px] text-text-grey flex items-center">
                <span className="w-2 h-2 bg-heritage-grey rounded-full mr-2"></span>
                Role: {item.role}
              </p>
              <p className="text-[13px] text-text-grey flex items-center">
                <span className="w-2 h-2 bg-heritage-grey rounded-full mr-2"></span>
                Email: {item.apiData?.user?.email || item.email}
              </p>
              <p className="text-[13px] text-text-grey flex items-center">
                <span className="w-2 h-2 bg-heritage-grey rounded-full mr-2"></span>
                Phone: {item.apiData?.user?.phone_number || item.phone || 'N/A'}
              </p>
              <p className="text-[13px] text-text-grey flex items-center">
                <span className="w-2 h-2 bg-heritage-grey rounded-full mr-2"></span>
                Documents: {item.documents?.length || 0} file(s)
              </p>
            </div>
          </div>
        )}
      />

      {/* Team Member Document Modal */}
      <TeamMemberDocumentModal
        isOpen={isDocumentModalVisible}
        onClose={handleDocumentModalClose}
        documents={selectedTeamMemberForDocs ? transformTeamMemberDocumentsForModal(selectedTeamMemberForDocs) : []}
        onDownload={handleDocumentDownload}
        title="Documents"
        teamMemberName={selectedTeamMemberForDocs?.name}
        projectId={Number(project.project.projectId)}
      />

      {/* Team Member Certificate Upload Modal */}
      <TeamMemberCertificateUploadModal
        isOpen={isCertificateUploadModalVisible}
        onClose={() => {
          setIsCertificateUploadModalVisible(false);
          setSelectedTeamMemberForUpload(null);
        }}
        onUpload={(files) => {
          // Handle the uploaded files
          if (files && files.length > 0) {
            // Mark that changes were made
            setHasChanges(true);
            // No need to manually refresh - the effects will handle it
            // Success notification is already shown by the modal
          }
        }}
        title="Upload Team Member Certificates"
        teamMember={selectedTeamMemberForUpload}
        projectId={Number(project.project.projectId)}
      />

      {/* Role Access Matrix Modal */}
      <Dialog open={isRoleAccessMatrixVisible} onOpenChange={setIsRoleAccessMatrixVisible}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-hidden">
          <div className="p-4">
            <RoleAccessMatrix
              onBack={() => setIsRoleAccessMatrixVisible(false)}
              projectId={project.project.projectId}
              isOpen={isRoleAccessMatrixVisible}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Viewer Modal */}
      <Dialog open={isQRCodeModalVisible} onOpenChange={setIsQRCodeModalVisible}>
        <DialogContent className="max-w-lg">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Welder QR Code</h2>
              {/* <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsQRCodeModalVisible(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </Button> */}
            </div>
            {selectedQRCode ? (
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200">
                  <img
                    src={selectedQRCode}
                    alt="Welder QR Code"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                    }}
                  />
                </div>
                <Button
                  //variant="outline"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedQRCode;
                    link.download = 'welder-qr-code.png';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    customNotification.success('Download Started', 'QR code is being downloaded...');
                  }}
                >
                  Download QR Code
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-gray-500">No QR code available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Qualification Details Modal */}
      <Dialog open={isQualificationModalVisible} onOpenChange={setIsQualificationModalVisible}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Qualifications - {selectedMemberQualifications?.name}
            </h2>
          </div>
          
          {selectedMemberQualifications?.details && (
            <div className="flex border rounded-lg overflow-hidden m-4">
              {/* Left Side - Qualifications Table */}
              <div className="flex-1 border-r">
                <div className="border-b text-center py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">
                  Qualifications
                </div>
                <Table containerClassName="">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center">Qualification Type</TableHead>
                      <TableHead className="text-center">Level Type</TableHead>
                      <TableHead className="text-center">Qualification ID</TableHead>
                      <TableHead className="text-center">Expiration Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedMemberQualifications.details.qualifications?.length > 0 ? (
                      selectedMemberQualifications.details.qualifications.map((qual: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell className="text-center">{qual.qualification_type}</TableCell>
                          <TableCell className="text-center">{qual.level_type}</TableCell>
                          <TableCell className="text-center">{qual.qualification_id || '-'}</TableCell>
                          <TableCell className="text-center">{qual.expiration_date}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No qualifications recorded
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Right Side - Eyesight Examination */}
              <div className="w-48 flex-shrink-0 flex flex-col">
                <div className="border-b text-center py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">
                  Eyesight Examination
                </div>
                <div className="flex-1 flex flex-col items-center justify-center py-6 px-4">
                  <p className={cn(
                    "text-base font-semibold mb-1",
                    selectedMemberQualifications.details.eyesight_examination_passed 
                      ? "text-green-600" 
                      : "text-red-600"
                  )}>
                    {selectedMemberQualifications.details.eyesight_examination_passed ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedMemberQualifications.details.eyesight_examination_expiry_date || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QC Inspection Personnel Report Modal */}
      <EquipmentSignatureReportModal
        isOpen={isQCReportModalOpen}
        onClose={() => setIsQCReportModalOpen(false)}
        registryItems={[]}
        registryType="QC Inspection Personnel"
        projectId={Number(project.project.projectId)}
        projectName={project.project.projectName}
        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
        isQCInspectorUser={isQCInspectorUser}
        isWelderUser={isWelderUser}
        currentUserTeamMemberId={currentUserTeamMemberId}
        canSign={true}
        canEdit={canEdit}
        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        onRefresh={fetchTeamMembers}
      />

      <EquipmentReportsViewModal
        isOpen={isReportsViewModalOpen}
        onClose={() => setIsReportsViewModalOpen(false)}
        projectId={project?.project?.projectId ? Number(project.project.projectId) : 0}
        projectName={project?.project?.projectName || ''}
        showOnlyQC={true}
      />
    </Card>
  );
}; 